import { QuestionCircleFilled } from '@ant-design/icons';
import { Select } from 'antd';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Tooltip } from 'components/ui';
import { useGetTurbineExternalServiceRequestNumbersQuery } from 'types/atlas-graphql';

import { type ServiceRequest } from './types';

const FlexContainer = styled.div`
  display: flex;
`;

interface IExternalServiceRequestChooserProps {
  assetId: string;
  onChange?: any;
  disableForMultipleTurbines?: boolean;
}

export const ExternalServiceRequestChooser: React.FunctionComponent<
  IExternalServiceRequestChooserProps
> = ({ assetId, onChange, disableForMultipleTurbines }) => {
  const { t } = useTranslation();
  const placeholder = t(
    'workOrder.serviceRequest.selectPlaceholder',
    'Select Service Request Number'
  );

  const { data, loading } = useGetTurbineExternalServiceRequestNumbersQuery({
    fetchPolicy: 'no-cache',
    skip: !assetId,
    variables: { assetId: assetId ?? '' },
  });

  const tooltip = disableForMultipleTurbines
    ? t(
        'workOrder.serviceRequest.multipleTurbinesWarning',
        'Work Order contains tasks for multiple turbines. Ensure tasks are limited to a single turbine'
      )
    : assetId !== '' && data?.asset?.externalServiceRequestNumbers?.serviceRequests?.length === 0
      ? t(
          'workOrder.serviceRequest.noneAvailableForAsset',
          'No active Service Requests available for {{name}}',
          { name: data?.asset.name }
        )
      : data?.asset?.externalServiceRequestNumbers?.success === false
        ? data?.asset?.externalServiceRequestNumbers?.message
        : assetId === ''
          ? t(
              'workOrder.serviceRequest.selectSingleTurbineToEnable',
              'To enable, select tasks for a single turbine'
            )
          : '';

  return (
    <FlexContainer>
      <Select
        loading={loading}
        optionFilterProp="children"
        onChange={value => onChange(value, data?.asset.name)}
        placeholder={placeholder}
        showSearch
        style={{ minWidth: '15rem' }}
        dropdownMatchSelectWidth={false}
        disabled={tooltip !== '' || assetId === ''}
      >
        {!loading && (
          <>
            {data?.asset?.externalServiceRequestNumbers?.serviceRequests?.length > 0 &&
              data?.asset?.externalServiceRequestNumbers?.serviceRequests?.map(
                (serviceRequest: ServiceRequest) => (
                  <Select.Option key={serviceRequest.sr} value={serviceRequest.sr}>
                    {`${serviceRequest.sr} | ${serviceRequest.status} | ${serviceRequest.summary}`}
                  </Select.Option>
                )
              )}
          </>
        )}
      </Select>
      <span style={{ marginRight: '0.4rem' }}></span>
      <Tooltip title={tooltip}>
        <QuestionCircleFilled style={{ marginRight: '0.4rem' }} />
      </Tooltip>
    </FlexContainer>
  );
};
